import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreateHSNCodeDto, HSNCode } from '../../services/hsncode.service';
import {
  Field,
  formatMoney,
  formatNumber,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { useActiveGstRates } from '../../hooks/useGstRates';

interface HSNCodeFormProps {
  // When set, the form edits this code; otherwise it creates a new one
  hsnCode?: HSNCode | null;
  onSubmit: (data: CreateHSNCodeDto) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}


const toNumber = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ step, title, description, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
        {step}
      </span>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

const HSNCodeForm: React.FC<HSNCodeFormProps> = ({ hsnCode, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!hsnCode;
  // The rates of the GST rate master; a code keeps showing its own rate after that one was retired
  const { optionsFor, defaultRate, isLoaded: ratesLoaded } = useActiveGstRates();
  const rateTouched = useRef(false);
  // Invoices store the code itself, so a code that is in use keeps its number
  const codeLocked = isEdit && (hsnCode?.invoiceLines ?? 0) > 0;

  const [form, setForm] = useState({
    code: hsnCode?.code || '',
    description: hsnCode?.description || '',
    unit: hsnCode?.unit || '',
    status: hsnCode && !hsnCode.isActive ? 'inactive' : 'active',
    gstRate: String(hsnCode?.gstRate ?? defaultRate),
    cessRate: hsnCode?.cessRate ? String(hsnCode.cessRate) : '',
    notes: hsnCode?.notes || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // A new code starts with the default rate of the master once it has loaded
  useEffect(() => {
    if (!isEdit && ratesLoaded && !rateTouched.current) {
      setForm((prev) => ({ ...prev, gstRate: String(defaultRate) }));
    }
  }, [isEdit, ratesLoaded, defaultRate]);

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [name]: name === 'code' ? value.replace(/\D/g, '').slice(0, 8) : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};

    if (!form.code) next.code = 'HSN code is required';
    else if (!/^[0-9]{4,8}$/.test(form.code)) next.code = 'An HSN code has 4 to 8 digits';

    const description = form.description.trim();
    if (!description) next.description = 'Description is required';
    else if (description.length < 3 || description.length > 500) next.description = 'Description must be 3 to 500 characters';

    const gstRate = Number(form.gstRate);
    if (form.gstRate.trim() === '' || !Number.isFinite(gstRate) || gstRate < 0 || gstRate > 100) {
      next.gstRate = 'Enter a GST rate from 0 to 100';
    }

    const cessRate = Number(form.cessRate || 0);
    if (!Number.isFinite(cessRate) || cessRate < 0 || cessRate > 999) next.cessRate = 'Enter a cess rate of 0 or more';

    if (form.unit.trim().length > 20) next.unit = 'Unit can be at most 20 characters';
    if (form.notes.length > 1000) next.notes = 'Notes can be at most 1000 characters';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateHSNCodeDto = {
      // A locked code is not sent, so the server does not see it as a change
      ...(codeLocked ? {} : { code: form.code }),
      description: form.description.trim(),
      gstRate: toNumber(form.gstRate),
      cessRate: toNumber(form.cessRate),
      // On an edit, null clears a value that was removed in the form
      unit: form.unit.trim() || (isEdit ? null : undefined),
      notes: form.notes.trim() || (isEdit ? null : undefined),
      isActive: form.status === 'active',
    } as CreateHSNCodeDto;

    try {
      await onSubmit(payload);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const gstRate = toNumber(form.gstRate);
  const cessRate = toNumber(form.cessRate);
  const half = round2(gstRate / 2);
  // What a sale of 1,000 rupees is charged, to make the rate concrete
  const sampleTax = round2((1000 * (gstRate + cessRate)) / 100);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Section step={1} title="Code" description="The HSN or SAC code and what it covers.">
            <Field
              label="HSN Code"
              htmlFor="hsn-code"
              required
              error={errors.code}
              hint={
                codeLocked
                  ? `Used on ${hsnCode?.invoiceLines} invoice ${hsnCode?.invoiceLines === 1 ? 'line' : 'lines'}, so the number cannot change.`
                  : '4 to 8 digits. The first two are the chapter.'
              }
            >
              <input
                id="hsn-code"
                type="text"
                inputMode="numeric"
                value={form.code}
                onChange={(e) => setField('code', e.target.value)}
                disabled={codeLocked}
                className={cn(fieldClass('code'), 'font-mono')}
                placeholder="e.g. 8708"
              />
            </Field>

            <Field label="Unit" htmlFor="hsn-unit" error={errors.unit} hint="The usual unit of measure, if there is one.">
              <input
                id="hsn-unit"
                type="text"
                maxLength={20}
                value={form.unit}
                onChange={(e) => setField('unit', e.target.value)}
                className={fieldClass('unit')}
                placeholder="e.g. pcs, kg"
              />
            </Field>

            <Field label="Description" htmlFor="hsn-description" required error={errors.description} className="sm:col-span-2">
              <textarea
                id="hsn-description"
                rows={2}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                className={fieldClass('description')}
                placeholder="What goods or services the code covers"
              />
            </Field>

            <Field
              label="Status"
              htmlFor="hsn-status"
              hint="An inactive code is kept for old invoices but no longer offered on new ones."
            >
              <select
                id="hsn-status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className={inputClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            <Field label="Notes" htmlFor="hsn-notes" error={errors.notes}>
              <textarea
                id="hsn-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                className={fieldClass('notes')}
                placeholder="Notification number, exceptions, anything worth remembering"
              />
            </Field>
          </Section>

          <Section step={2} title="Tax rates" description="One GST rate; the split into CGST, SGST and IGST follows from it.">
            <Field label="GST Rate (%)" required error={errors.gstRate} className="sm:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                {optionsFor(hsnCode?.gstRate).map((slab) => (
                  <button
                    key={slab}
                    type="button"
                    onClick={() => {
                      rateTouched.current = true;
                      setField('gstRate', String(slab));
                    }}
                    aria-pressed={gstRate === slab && form.gstRate.trim() !== ''}
                    className={cn(
                      'rounded-md px-3 py-2 text-sm font-semibold ring-1 ring-inset',
                      gstRate === slab && form.gstRate.trim() !== ''
                        ? 'bg-indigo-600 text-white ring-indigo-600'
                        : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50',
                    )}
                  >
                    {slab}%
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Rates come from the GST rate master.{' '}
                <Link to="/master-data/gst-rates" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Manage GST rates
                </Link>
              </p>
            </Field>

            <Field
              label="Cess (%)"
              htmlFor="hsn-cess"
              error={errors.cessRate}
              hint="Compensation cess on top of GST. Most goods have none."
            >
              <input
                id="hsn-cess"
                type="number"
                min="0"
                step="0.01"
                value={form.cessRate}
                onChange={(e) => setField('cessRate', e.target.value)}
                className={fieldClass('cessRate')}
                placeholder="0"
              />
            </Field>
          </Section>
        </div>

        {/* Right: how the rate is charged */}
        <aside className="lg:col-span-1">
          <div className="space-y-5 lg:sticky lg:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                How it is charged
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">HSN code</p>
                  <p className="font-mono font-medium text-gray-900">{form.code || '—'}</p>
                  {form.code.length >= 2 && <p className="text-xs text-gray-500">Chapter {form.code.slice(0, 2)}</p>}
                </div>

                <div className="rounded-md bg-indigo-50 px-3 py-3">
                  <p className="text-xs text-indigo-700">GST rate</p>
                  <p className="text-2xl font-semibold text-indigo-900 tabular-nums">{formatNumber(gstRate, 2)}%</p>
                </div>

                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Sale in the same state</dt>
                    <dd className="text-right font-medium text-gray-900 tabular-nums">
                      CGST {formatNumber(half, 2)}% + SGST {formatNumber(half, 2)}%
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Sale to another state</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">IGST {formatNumber(gstRate, 2)}%</dd>
                  </div>
                  {cessRate > 0 && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">Cess</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatNumber(cessRate, 2)}%</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-3 border-t border-gray-200 pt-2">
                    <dt className="text-gray-600">Tax on {formatMoney(1000)}</dt>
                    <dd className="font-semibold text-gray-900 tabular-nums">{formatMoney(sampleTax)}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">New invoice lines with this code are taxed at {formatNumber(gstRate, 2)}%</span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update HSN Code' : 'Create HSN Code'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default HSNCodeForm;
